import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, CheckCircle2 } from "lucide-react";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const forced = user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) return setError("New passwords do not match");
    if (next.length < 8) return setError("New password must be at least 8 characters");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      const u = await refresh();
      navigate(u?.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm">
        <div className="w-11 h-11 rounded-md bg-[#FF4500] flex items-center justify-center mb-5">
          <KeyRound className="w-6 h-6 text-white" />
        </div>
        <h1 className="font-display text-2xl font-semibold">
          {forced ? "Set your password" : "Change password"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 mb-8">
          {forced
            ? "Your PAN is not a secret, so please choose a new private password to continue."
            : "Update the password you use to sign in."}
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <Label className="label-caps">{forced ? "Current password (your PAN)" : "Current password"}</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
              required className="mt-1.5" data-testid="current-password-input" />
          </div>
          <div>
            <Label className="label-caps">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
              required className="mt-1.5" data-testid="new-password-input" placeholder="At least 8 characters" />
          </div>
          <div>
            <Label className="label-caps">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              required className="mt-1.5" data-testid="confirm-password-input" />
          </div>
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="change-password-error">
              {error}
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full h-11" data-testid="change-password-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><CheckCircle2 className="w-4 h-4" /> Save password</>)}
          </Button>
        </form>
        {forced && (
          <button onClick={async () => { await logout(); navigate("/login"); }}
            className="text-xs text-muted-foreground mt-6 hover:text-foreground" data-testid="change-password-cancel">
            Sign in as a different user
          </button>
        )}
      </div>
    </div>
  );
}
